import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Settings as SettingsIcon, ShieldCheck } from 'lucide-react';
import { Button } from '../ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';
import MobileAccountSection from './MobileAccountSection';
import MobileTabBar from '../mobile/MobileTabBar';
import MobileMoreMenu from '../mobile/MobileMoreMenu';
import MobileCalculatorMenu from '../mobile/MobileCalculatorMenu';

/**
 * MobileSettingsLayout
 * Single-column accordion layout used at viewports below 768px.
 * Wraps the existing form state (passed down from Settings.js) so we don't
 * duplicate any save/load logic. Desktop layout in Settings.js is untouched.
 *
 * Renders the primary MobileTabBar at the bottom so users can jump directly to
 * any other primary section instead of being stranded behind the back arrow.
 * Branding & Contact intentionally lives under the "More" menu only — not here —
 * so it is not duplicated on the Settings page.
 */
const MobileSettingsLayout = (_props) => {
  // Settings.js still passes agentProfile / onInputChange / onSave /
  // isSaving / hasUnsavedChanges for legacy reasons; we accept and ignore
  // them here so the prop signature stays stable for callers.
  const navigate = useNavigate();

  // Tab-bar state — mirrors MobileLayout.js exactly so tapping any tab
  // behaves the same way it does from Dashboard or a calculator page.
  // Settings is reached from More → Settings, so 'more' is the active tab.
  const [activeTab, setActiveTab] = useState('more');
  const [showMoreMenu, setShowMoreMenu] = useState(false);
  const [showCalculatorMenu, setShowCalculatorMenu] = useState(false);

  const handleTabClick = (tabId, route) => {
    setActiveTab(tabId);

    if (tabId !== 'calculators' && showCalculatorMenu) {
      setShowCalculatorMenu(false);
    }

    if (tabId === 'more') {
      setShowMoreMenu(true);
      return;
    }

    if (tabId === 'calculators') {
      setShowCalculatorMenu(true);
      return;
    }

    if (tabId === 'actions') {
      navigate('/dashboard?panel=actiontracker');
      return;
    }

    if (tabId === 'finances') {
      navigate('/dashboard?panel=pnl');
      return;
    }

    if (route) {
      navigate(route);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50" data-testid="mobile-settings-page">
      {/* Mobile header — sits BELOW the iOS status bar via safe-area padding.
          Back arrow left, title centered, nothing else. Back button is 44×44
          per Apple HIG touch-target rules. */}
      <header
        className="bg-white border-b border-gray-200 sticky top-0 z-20"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <div className="relative h-12 flex items-center">
          <button
            type="button"
            onClick={() => navigate(-1)}
            aria-label="Back"
            className="absolute left-1 inset-y-0 w-11 h-11 my-auto flex items-center justify-center text-gray-900 rounded-full hover:bg-gray-100 active:bg-gray-200 transition-colors"
            data-testid="mobile-settings-back"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <h1
            className="mx-auto text-base font-semibold text-gray-900"
            data-testid="mobile-settings-title"
          >
            Settings
          </h1>
        </div>
      </header>

      {/* pb-28 keeps the bottom accordion clear of the fixed MobileTabBar */}
      <div className="px-4 py-4 pb-28 space-y-3">
        <Accordion
          type="multiple"
          defaultValue={['account']}
          className="space-y-3"
        >
          {/* 1. Account */}
          <AccordionItem
            value="account"
            className="rounded-xl border border-gray-200 bg-gray-50 px-4 border-b"
          >
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-emerald-100 flex items-center justify-center">
                  <ShieldCheck className="w-5 h-5 text-emerald-600" />
                </div>
                <span className="text-base font-semibold text-gray-900">Account</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-2">
              <MobileAccountSection />
            </AccordionContent>
          </AccordionItem>

          {/* 2. Business Setup */}
          <AccordionItem
            value="business"
            className="rounded-xl border border-gray-200 bg-white px-4 border-b"
          >
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-100 flex items-center justify-center">
                  <SettingsIcon className="w-5 h-5 text-amber-600" />
                </div>
                <span className="text-base font-semibold text-gray-900">Business Setup</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-2">
              <p className="text-sm text-gray-600 mb-3">
                Update your annual goals, commission structure, weekly hours, and focus areas.
                Your AI Coach will adjust your daily plan based on these settings.
              </p>
              <Button
                onClick={() => navigate('/onboarding')}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
                data-testid="mobile-open-business-setup"
              >
                Open Business Setup
              </Button>
            </AccordionContent>
          </AccordionItem>

          {/* 3. Preferences (placeholder, mirrors current desktop disabled tab) */}
          <AccordionItem
            value="prefs"
            className="rounded-xl border border-gray-200 bg-white px-4 border-b"
          >
            <AccordionTrigger className="hover:no-underline">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-purple-100 flex items-center justify-center">
                  <SettingsIcon className="w-5 h-5 text-purple-600" />
                </div>
                <span className="text-base font-semibold text-gray-900">Preferences</span>
              </div>
            </AccordionTrigger>
            <AccordionContent className="pt-2">
              <p className="text-sm text-gray-500">
                More preferences are coming soon.
              </p>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      {/* Bottom tab nav — identical to MobileLayout so the user can jump to
          any primary section instead of being stranded behind the back arrow. */}
      <MobileTabBar activeTab={activeTab} onTabClick={handleTabClick} />

      <MobileMoreMenu
        isOpen={showMoreMenu}
        onClose={() => setShowMoreMenu(false)}
      />
      <MobileCalculatorMenu
        isOpen={showCalculatorMenu}
        onClose={() => setShowCalculatorMenu(false)}
      />
    </div>
  );
};

export default MobileSettingsLayout;
